// ============================================================================
// SPRINTFILTERRINGS.JS — Filtros de estado del sprint en formato anillo compacto
// ============================================================================
// Sustituye a las antiguas HeroCards: mismos filtros (sprintStatusFilter del
// store) pero comprimidos a 3 anillos sutiles que viven en la cabecera del
// sprint, a la izquierda del botón "Cerrar sprint". Tooltip + color semántico
// + resaltado del filtro activo. Clic en un anillo activo = quitar filtro.
// ============================================================================

import React, { useMemo } from 'react';
import { useTaskStore } from '../store/TaskStore';
import { shallow } from 'zustand/shallow';

// Orden de izquierda a derecha = flujo natural de progreso.
const RINGS = [
  { id: 'pendiente', label: 'Pendientes', color: '#FF8B00' },
  { id: 'en-curso', label: 'En curso', color: '#0052CC' },
  { id: 'completada', label: 'Completadas', color: '#00875A' },
];

const RING_SIZE = 28;
const RING_STROKE = 3;

function SprintFilterRings() {
  const { tasks, activeSprint, sprintStatusFilter, setSprintStatusFilter } = useTaskStore(
    (s) => ({
      tasks: s.tasks,
      activeSprint: s.activeSprint,
      sprintStatusFilter: s.sprintStatusFilter,
      setSprintStatusFilter: s.setSprintStatusFilter
    }),
    shallow
  );

  // Conteo por estado SOLO de las tareas del sprint activo (igual criterio que
  // las HeroCards anteriores).
  const counts = useMemo(() => {
    const base = { pendiente: 0, 'en-curso': 0, completada: 0, total: 0 };
    if (!activeSprint) return base;
    const sprintTasks = tasks.filter(t => t.sprintActual === activeSprint.id);
    base.total = sprintTasks.length;
    base.pendiente = sprintTasks.filter(t => t.estado === 'pendiente').length;
    base['en-curso'] = sprintTasks.filter(t => t.estado === 'en-curso').length;
    base.completada = sprintTasks.filter(t => t.estado === 'completada').length;
    return base;
  }, [tasks, activeSprint]);

  // Sin sprint activo no hay nada que filtrar.
  if (!activeSprint) return null;

  const hasFilter = sprintStatusFilter.length > 0;
  const isActive = (id) => sprintStatusFilter.length === 1 && sprintStatusFilter[0] === id;

  const handleClick = (id) => {
    if (isActive(id)) {
      setSprintStatusFilter([]); // toggle off → ver todas
    } else {
      setSprintStatusFilter([id]);
    }
  };

  const radius = (RING_SIZE - RING_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="sprint-filter-rings" role="group" aria-label="Filtrar tareas del sprint por estado">
      {RINGS.map(({ id, label, color }) => {
        const count = counts[id];
        const pct = counts.total > 0 ? (count / counts.total) * 100 : 0;
        const offset = circumference - (pct / 100) * circumference;
        const empty = count === 0;
        const active = isActive(id);
        // Cuando hay un filtro activo, los demás anillos se atenúan para
        // destacar el seleccionado.
        const dimmed = hasFilter && !active;
        const accent = empty ? '#a0a6b0' : color;

        return (
          <button
            key={id}
            type="button"
            className={`sfr-ring${active ? ' active' : ''}${dimmed ? ' dimmed' : ''}`}
            style={active ? { '--sfr-accent': accent } : undefined}
            onClick={() => handleClick(id)}
            title={active ? `${label} (quitar filtro)` : `Filtrar: ${label}`}
            aria-pressed={active}
            aria-label={`${label}: ${count}`}
          >
            <svg width={RING_SIZE} height={RING_SIZE} className="sfr-svg">
              <circle
                className="sfr-track"
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={radius}
                fill="none"
                strokeWidth={RING_STROKE}
              />
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={radius}
                fill="none"
                strokeWidth={RING_STROKE}
                stroke={accent}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                className="sfr-progress"
              />
            </svg>
            <span className="sfr-num" style={{ color: accent }}>{count}</span>
          </button>
        );
      })}
    </div>
  );
}

export default SprintFilterRings;
